import React, { useEffect, useMemo } from "react";
import { Table } from "antd";
import "../styles/globalTable.css";
import TableLoader from "./tableLoader";
import PaginationTable from "./paginationTable";
import { useAntdColumnResize } from "react-antd-column-resize";

export const GlobalTable = ({
  columns,
  dataSource,
  pagination,
  rowSelection,
  onRow,
  onChange,
  loading,
  resize,
  resetTrigger,
}) => {
  const tableHeaderClassName = "my-table-header";
  const modifiedRowSelection = rowSelection && {
    ...rowSelection,
    fixed: "left",
  };

  // Use the resize hook
  const { resizableColumns, components, resetColumns } = useAntdColumnResize(
    () => ({ columns }),
    [columns]
  );

  // Merge the original onHeaderCell with resize functionality
  const mergedColumns = useMemo(() => {
    if (!resizableColumns || resizableColumns.length === 0) return columns;
    return resizableColumns.map((resizableCol, index) => {
      const originalCol = columns[index];
      if (!originalCol) return resizableCol;
      const originalOnHeaderCell = originalCol.onHeaderCell;
      if (!originalOnHeaderCell) {
        return resizableCol;
      }
      return {
        ...resizableCol,
        onHeaderCell: (column) => {
          // Get the resize handler props
          const resizeProps = resizableCol.onHeaderCell
            ? resizableCol.onHeaderCell(column)
            : {};
          // Get the original handler props
          const originalProps = originalOnHeaderCell(column);
          // Merge onClick handlers
          return {
            ...resizeProps,
            ...originalProps,
            onClick: (e) => {
              // Check if clicking on resize handle
              const isResizeHandle =
                e.target.classList.contains("react-resizable-handle") ||
                e.target.closest(".react-resizable-handle");
              // Only trigger original onClick if not clicking resize handle
              if (!isResizeHandle && originalProps.onClick) {
                originalProps.onClick(e);
              }
              // Call resize onClick if exists
              if (resizeProps.onClick) {
                resizeProps.onClick(e);
              }
            },
          };
        },
      };
    });
  }, [resizableColumns, columns]);

  useEffect(() => {
    resetColumns();
  }, [resetTrigger]);

  return (
    <div className="my-table">
      {/* Show TableLoader while loading */}
      {loading && <TableLoader isLoading={loading} rowCount={10} />}
      {/* Render table only when not loading */}
      {!loading && (
        <Table
          columns={mergedColumns}
          dataSource={dataSource}
          pagination={false}
          rowSelection={modifiedRowSelection}
          onRow={onRow}
          headerClassName={tableHeaderClassName}
          showSorterTooltip={false}
          onChange={onChange}
          components={resize ? components : undefined}
          scroll={resize ? { x: "max-content" } : undefined}
        />
      )}
      {/* Show pagination only when not loading and there is data */}
      {!loading && pagination !== false && pagination?.total > 0 && (
        <PaginationTable
          total={pagination.total}
          pagination={pagination}
          paginationConfig={pagination}
        />
      )}
    </div>
  );
};
